/**
 * useEquipmentTypes — справочник «Тип оборудования» организации
 * (equipment_types_ref).
 *
 * Точная копия приёмов usePropertyTypes/useCities: кэш на модуле (форма
 * оборудования, Настройки — обе точки читают один и тот же список), словарь
 * меняется на порядки реже, чем читается. Правка из другой вкладки
 * подхватится после reload — тот же осознанный компромисс.
 *
 * В отличие от типов объекта, здесь нет чекбокса «Жильё» и других
 * поведенческих полей — тип ни на что в коде не влияет, это чистая категория.
 */
import { useEffect, useState } from 'react';
import { api } from '../api/client';
import { track } from '../analytics';

let cache = null;          // Promise<EquipmentType[]>|null
let cachedCanEdit = false; // последнее известное значение — для мгновенного возврата новым подписчикам

function load() {
  if (!cache) {
    cache = api.get('/equipment/types-ref')
      .then(r => {
        cachedCanEdit = !!r.can_edit;
        return r.types || [];
      })
      .catch(() => []);
  }
  return cache;
}

/** Сбросить кэш — после добавления/переименования/архивации типа. */
export function invalidateEquipmentTypes() {
  cache = null;
}

export function useEquipmentTypes({ enabled = true } = {}) {
  const [types, setTypes] = useState([]);
  const [canEdit, setCanEdit] = useState(cachedCanEdit);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!enabled) return;
    let alive = true;
    setLoading(true);
    load().then(t => {
      if (!alive) return;
      setTypes(t || []);
      setCanEdit(cachedCanEdit);
      setLoading(false);
    });
    return () => { alive = false; };
  }, [enabled]);

  const reload = async () => {
    invalidateEquipmentTypes();
    const t = await load();
    setTypes(t || []);
    setCanEdit(cachedCanEdit);
    return t;
  };

  const addType = async (label) => {
    const r = await api.post('/equipment/types-ref', { label_ru: label });
    track('equipment_type_edit', { action: r.created ? 'add' : 'reuse' });
    await reload();
    return r.type;
  };

  // Правим то название, которое человек видит: интерфейс показывает label_en на
  // английском и label_ru на русском. Раньше уходил только label_ru, и у типов с
  // ручным английским (импорт, правки) на английском интерфейсе правка «не
  // сохранялась» — на экране оставалось старое label_en.
  // Если обе подписи совпадали (один язык на оба) — обновляем обе, иначе вторая
  // останется со старым текстом.
  const renameType = async (typeId, label, lang) => {
    const cur = types.find(t => t.id === typeId);
    const same = !!cur && !!cur.label_ru && cur.label_ru === cur.label_en;
    const body = same ? { label_ru: label, label_en: label }
      : (lang === 'en' ? { label_en: label } : { label_ru: label });
    const row = await api.put(`/equipment/types-ref/${typeId}`, body);
    track('equipment_type_edit', { action: 'rename' });
    invalidateEquipmentTypes();
    setTypes(prev => prev.map(t => (t.id === typeId ? { ...t, ...row } : t)));
    return row;
  };

  const archiveType = async (typeId) => {
    const r = await api.delete(`/equipment/types-ref/${typeId}`);
    track('equipment_type_edit', { action: 'archive' });
    invalidateEquipmentTypes();
    setTypes(prev => prev.filter(t => t.id !== typeId));
    return r;
  };

  // Драг-н-дроп в Настройках — тот же приём, что reorderTypes/reorderCities:
  // стейт уже переставлен вызывающим, здесь фиксируем на сервере и сбрасываем
  // кэш модуля, чтобы форма оборудования не отдавала старый порядок до
  // следующей перезагрузки страницы.
  const reorderTypes = async (reordered) => {
    setTypes(reordered);
    try {
      await api.patch('/equipment/types-ref/reorder', { ids: reordered.map(t => t.id) });
      track('equipment_type_edit', { action: 'reorder' });
      invalidateEquipmentTypes();
    } catch (e) {
      await reload(); // откат к серверному порядку
      throw e;
    }
  };

  return { types, loading, canEdit, reload, addType, renameType, archiveType, reorderTypes };
}

/** Ярлык типа на языке интерфейса с честным фолбэком на второй. */
export function equipmentTypeLabel(row, lang) {
  if (!row) return '';
  return (lang === 'en' ? (row.label_en || row.label_ru) : (row.label_ru || row.label_en)) || '';
}

/** Лейбл по ключу типа (equipment.type) — для списков/карточек оборудования. */
export function equipmentTypeLabelByKey(types, key, lang) {
  if (!key) return '';
  return equipmentTypeLabel((types || []).find(t => t.key === key), lang);
}
